(() => {
  function mountMegaNavPromos() {
    document.querySelectorAll('[data-mega-nav-promo-slot]').forEach((slot) => {
      if (!(slot instanceof HTMLElement)) return;

      const handle = slot.dataset.megaNavPromoSlot ?? '';
      const source = document.querySelector(
        `.mega-nav-promo-source .mega-nav-panel__promo-match[data-menu-promo-for="${handle}"]`
      );
      if (!(source instanceof HTMLElement)) return;

      if (slot.querySelector(`[data-menu-promo-for="${handle}"]`)) return;

      slot.replaceChildren(source.cloneNode(true));
    });
  }

  function initMegaNavPromoMounting() {
    mountMegaNavPromos();

    if (window.__megaNavPromoObserver) return;

    const headerSection = document.querySelector('.header-section');
    if (!headerSection) return;

    window.__megaNavPromoObserver = new MutationObserver(() => {
      mountMegaNavPromos();
    });

    window.__megaNavPromoObserver.observe(headerSection, {
      attributes: true,
      attributeFilter: ['data-hydrated'],
      childList: true,
      subtree: true,
    });
  }

  if (!window.__megaNavPromoSectionListener) {
    window.__megaNavPromoSectionListener = true;
    document.addEventListener('shopify:section:load', mountMegaNavPromos);
  }

  if (customElements.get('mega-nav-menu')) {
    mountMegaNavPromos();
    return;
  }

  /**
   * Desktop megamenu — single dropdown panel, stable hover behavior.
   */
  class MegaNavMenu extends HTMLElement {
    /** @type {number | null} */
    #activeIndex = null;

    /** @type {ReturnType<typeof setTimeout> | undefined} */
    #closeTimer;

    /** @type {ReturnType<typeof setTimeout> | undefined} */
    #openTimer;

    /** @type {number} */
    #closeGeneration = 0;

    /** @type {ReturnType<typeof setTimeout> | undefined} */
    #closeFinishTimer;

    /** @type {MutationObserver | undefined} */
    #hydrationObserver;

    connectedCallback() {
      this.#bindTriggers();
      this.addEventListener('keydown', this.#onKeyDown);
      document.addEventListener('click', this.#onDocumentClick);
      window.addEventListener('resize', this.#positionDropdown);
      window.addEventListener('scroll', this.#positionDropdown, { passive: true });
      this.#positionDropdown();
      this.#watchSectionHydration();
      initMegaNavPromoMounting();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.#onDocumentClick);
      window.removeEventListener('resize', this.#positionDropdown);
      window.removeEventListener('scroll', this.#positionDropdown);
      this.#hydrationObserver?.disconnect();
      this.#hydrationObserver = undefined;
      this.#clearTimers();
      this.#cancelCloseFinish();
    }

    #watchSectionHydration() {
      const section = this.closest('.shopify-section');
      if (!(section instanceof HTMLElement)) return;
      if (section.dataset.hydrated === 'true') return;

      this.#hydrationObserver?.disconnect();
      this.#hydrationObserver = new MutationObserver(() => {
        if (section.dataset.hydrated !== 'true') return;
        this.#hydrationObserver?.disconnect();
        this.#hydrationObserver = undefined;
        this.#syncOpenStateAfterHydration();
      });

      this.#hydrationObserver.observe(section, {
        attributes: true,
        attributeFilter: ['data-hydrated'],
      });
    }

    #syncOpenStateAfterHydration() {
      const hoveredTrigger = this.querySelector('[data-mega-nav-index]:hover');
      const hoveringDropdown = this.querySelector('.mega-nav-menu__dropdown:hover');
      const stillInteracting = Boolean(hoveredTrigger || hoveringDropdown || this.classList.contains('is-open'));

      if (!stillInteracting) {
        this.#activeIndex = null;
        return;
      }

      const index =
        hoveredTrigger?.getAttribute('data-mega-nav-index') ??
        (this.#activeIndex != null ? String(this.#activeIndex) : null);
      const trigger = index != null ? this.querySelector(`[data-mega-nav-index="${index}"]`) : null;

      if (trigger instanceof Element) {
        this.#open(trigger);
      }
    }

    #positionDropdown = () => {
      const headerRow =
        this.closest('.header__row--top') ||
        this.closest('header-component')?.querySelector('.header__row--top');
      const header = this.closest('header-component') || document.querySelector('header-component');
      const anchor = headerRow ?? header;
      if (!anchor) return;

      const bottom = Math.round(anchor.getBoundingClientRect().bottom);
      this.style.setProperty('--mega-nav-dropdown-top', `${bottom}px`);
    };

    #bindTriggers() {
      this.querySelectorAll('[data-mega-nav-index]').forEach((trigger) => {
        trigger.addEventListener('mouseenter', () => this.#scheduleOpen(trigger));
        trigger.addEventListener('focus', () => this.#open(trigger));
        trigger.addEventListener('mouseleave', (event) => this.#scheduleClose(event));
      });

      this.querySelector('.mega-nav-menu__backdrop')?.addEventListener('click', () => this.#close());

      const dropdown = this.querySelector('.mega-nav-menu__dropdown');
      dropdown?.addEventListener('mouseenter', () => this.#clearTimers());
      dropdown?.addEventListener('mouseleave', (event) => this.#scheduleClose(event));

      this.addEventListener('mouseleave', (event) => {
        if (!this.contains(event.relatedTarget)) {
          this.#scheduleClose(event, 120);
        }
      });
    }

    #scheduleOpen(/** @type {Element} */ trigger) {
      this.#clearTimers();
      this.#openTimer = setTimeout(() => this.#open(trigger), 40);
    }

    #scheduleClose(/** @type {Event} */ event, /** @type {number} */ delay = 180) {
      const related = /** @type {MouseEvent} */ (event).relatedTarget;
      if (related instanceof Node && this.contains(related)) return;

      this.#clearTimers();
      this.#closeTimer = setTimeout(() => this.#close(), delay);
    }

    #clearTimers() {
      clearTimeout(this.#closeTimer);
      clearTimeout(this.#openTimer);
      this.#closeTimer = undefined;
      this.#openTimer = undefined;
    }

    #cancelCloseFinish() {
      this.#closeGeneration += 1;
      clearTimeout(this.#closeFinishTimer);
      this.#closeFinishTimer = undefined;
    }

    #finalizeClose(/** @type {number} */ generation) {
      if (generation !== this.#closeGeneration) return;

      const { dropdown, backdrop } = this.#getDropdownElements();
      dropdown?.setAttribute('aria-hidden', 'true');
      backdrop?.setAttribute('aria-hidden', 'true');

      this.querySelectorAll('[data-mega-nav-panel]').forEach((panel) => {
        panel.hidden = true;
        panel.classList.remove('is-active');
      });
    }

    #open(/** @type {Element} */ trigger) {
      const index = trigger.getAttribute('data-mega-nav-index');
      if (index == null) return;

      this.#clearTimers();
      this.#cancelCloseFinish();
      this.#positionDropdown();
      mountMegaNavPromos();
      this.#activeIndex = Number(index);
      this.classList.add('is-open');

      this.querySelectorAll('[data-mega-nav-index]').forEach((btn) => {
        const isActive = btn.getAttribute('data-mega-nav-index') === index;
        btn.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      });

      const { dropdown, backdrop } = this.#getDropdownElements();
      dropdown?.removeAttribute('aria-hidden');
      backdrop?.removeAttribute('aria-hidden');

      this.querySelectorAll('[data-mega-nav-panel]').forEach((panel) => {
        const isActive = panel.getAttribute('data-mega-nav-panel') === index;
        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);
      });
    }

    #close() {
      if (!this.classList.contains('is-open')) return;

      this.#clearTimers();
      this.#cancelCloseFinish();
      this.#activeIndex = null;
      this.classList.remove('is-open');

      this.querySelectorAll('[data-mega-nav-index]').forEach((btn) => {
        btn.setAttribute('aria-expanded', 'false');
      });

      const generation = this.#closeGeneration;
      const { dropdown } = this.#getDropdownElements();

      const finish = () => this.#finalizeClose(generation);

      if (dropdown instanceof HTMLElement) {
        let closed = false;
        const finishOnce = () => {
          if (closed) return;
          closed = true;
          dropdown.removeEventListener('transitionend', onTransitionEnd);
          clearTimeout(this.#closeFinishTimer);
          finish();
        };

        const onTransitionEnd = (/** @type {TransitionEvent} */ event) => {
          if (event.target !== dropdown || event.propertyName !== 'opacity') return;
          finishOnce();
        };

        dropdown.addEventListener('transitionend', onTransitionEnd);
        this.#closeFinishTimer = setTimeout(finishOnce, 320);
      } else {
        finish();
      }
    }

    #getDropdownElements() {
      return {
        dropdown: this.querySelector('.mega-nav-menu__dropdown'),
        backdrop: this.querySelector('.mega-nav-menu__backdrop'),
      };
    }

    #onKeyDown = (/** @type {KeyboardEvent} */ event) => {
      if (event.key === 'Escape') this.#close();
    };

    #onDocumentClick = (/** @type {MouseEvent} */ event) => {
      if (!(event.target instanceof Node) || !this.contains(event.target)) {
        this.#close();
      }
    };
  }

  customElements.define('mega-nav-menu', MegaNavMenu);
})();
